import { Injectable } from '@angular/core';
import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { from, Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

/**
 * User profile information extracted from Cognito ID token
 */
export interface CognitoUser {
  username: string;
  email: string;
  emailVerified: boolean;
  givenName: string;
  familyName: string;
  name: string;
  groups: string[];
}

/**
 * Custom error class for authentication-specific errors
 */
export class AppAuthError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * AuthService
 *
 * Handles AWS Cognito authentication using Amplify v6.
 *
 * Key responsibilities:
 * - Get JWT access token for API requests
 * - Extract user profile from ID token
 * - Check authentication status
 * - Sign out users
 * - Extract Cognito groups for authorization
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  /**
   * Gets the JWT access token from the current AWS Cognito session
   *
   * @returns Promise resolving to JWT access token string
   * @throws AppAuthError if user is not authenticated
   */
  async getJwtToken(): Promise<string> {
    try {
      const { accessToken } = (await fetchAuthSession()).tokens ?? {};

      if (!accessToken) {
        throw new Error('No authenticated user found');
      }

      return accessToken.toString();
    } catch (err) {
      console.error('Error fetching JWT token:', err);
      throw new AppAuthError('Current user is not authenticated');
    }
  }

  /**
   * Gets the current authenticated user with profile information
   *
   * Extracts user attributes from ID token payload and groups from access token.
   *
   * @returns Observable<CognitoUser> with user profile and groups
   */
  getCurrentUser(): Observable<CognitoUser> {
    return from(fetchAuthSession()).pipe(
      map((session) => {
        const { idToken, accessToken } = session.tokens ?? {};

        if (!idToken || !accessToken) {
          throw new AppAuthError('No authenticated user found');
        }

        // Extract user attributes from ID token payload
        const idPayload = idToken.payload;
        const groups = (accessToken.payload['cognito:groups'] as string[]) || [];

        return {
          username: (idPayload['cognito:username'] as string) || '',
          email: (idPayload['email'] as string) || '',
          emailVerified: (idPayload['email_verified'] as boolean) || false,
          givenName: (idPayload['given_name'] as string) || '',
          familyName: (idPayload['family_name'] as string) || '',
          name: (idPayload['name'] as string) || '',
          groups: groups
        };
      }),
      catchError((err) => {
        console.error('Error getting current user:', err);
        return throwError(() => new AppAuthError('Failed to get current user'));
      })
    );
  }

  /**
   * Checks if user is currently authenticated
   *
   * @returns Observable<boolean> true if authenticated, false otherwise
   */
  isAuthenticated(): Observable<boolean> {
    return from(fetchAuthSession()).pipe(
      map((session) => {
        return !!(session.tokens?.accessToken);
      }),
      catchError(() => {
        return from([false]);
      })
    );
  }

  /**
   * Gets Cognito groups for the current user
   *
   * @returns Observable<string[]> array of group names
   */
  getUserGroups(): Observable<string[]> {
    return from(fetchAuthSession()).pipe(
      map((session) => {
        const { accessToken } = session.tokens ?? {};

        if (!accessToken) {
          return [];
        }

        return (accessToken.payload['cognito:groups'] as string[]) || [];
      }),
      catchError((err) => {
        console.error('Error getting user groups:', err);
        return from([[]]);
      })
    );
  }

  /**
   * Redirects user to AWS Cognito federated sign-in page
   */
  federatedSignIn(): void {
    signInWithRedirect();
  }

  /**
   * Signs out the current user and clears Cognito session
   *
   * @returns Promise<void>
   */
  async signOutUser(): Promise<void> {
    try {
      await signOut();
    } catch (err) {
      console.error('Error signing out:', err);
      throw err;
    }
  }
}
